import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent
} from '@/components/ui/chart';
import {
  PURCHASE_CONVERSION_RATE_LABEL,
  calculateInterval,
  calculateAverageForPurchaseConversionRate,
  X_AXIS_KEY,
  getDefaultDateRange,
  VISITORS_RECOGNIZED_LABEL,
  transformMetricToTimeSeries,
  ANON_VISITORS_CAPTURED_LABEL,
  transformMultipleMetricsToTimeSeries,
  formatNumber,
  ChartOperation
} from '@/utils/dashboard-utils';
import { slugToMerchantId } from '@/utils/util';
import { useQuery, NetworkStatus } from '@apollo/client';
import {
  GET_ANON_VISITORS_CAPTURED,
  GET_CONVERSION_MONITORING_METRICS,
  GET_SERVER_EVENT_LOGS,
  GET_TOTAL_EVENTS_TRACKED,
  GET_VISITORS_RECOGNIZED
} from '@/queries/metrics';
import {
  GetConversionMonitoringMetricsQuery,
  GetConversionMonitoringMetricsQueryVariables,
  GetVisitorsRecognizedQueryVariables,
  GetVisitorsRecognizedQuery,
  GetAnonVisitorsCapturedQuery,
  GetAnonVisitorsCapturedQueryVariables,
  GetTotalEventsTrackedQueryVariables,
  GetTotalEventsTrackedQuery,
  GetServerEventLogsQueryVariables,
  GetServerEventLogsQuery
} from '@/gql/graphql';
import { ServerEventsLog } from './server-event-logs';
import { useSearchParams, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { TimeSeriesDataPoint } from '@/utils/dashboard-utils';
import { RefreshButton } from '@/components/refresh-button';
import { useMemo } from 'react';
import { AppActions, AppTitle, AppTitleText } from '@/components/app-header/app-header';
import { ControlledDatePicker } from '@/components/date-picker';
import { CardHeaderSkeleton, ChartSkeleton } from '@/components/ui/skeletons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

const purchaseConversionRateConfig = {
  [PURCHASE_CONVERSION_RATE_LABEL]: {
    label: PURCHASE_CONVERSION_RATE_LABEL,
    color: 'hsl(var(--chart-2))'
  }
} satisfies ChartConfig;
const visitorsRecognizedConfig = {
  [VISITORS_RECOGNIZED_LABEL]: { label: VISITORS_RECOGNIZED_LABEL, color: 'hsl(var(--chart-1))' }
} satisfies ChartConfig;

const anonVisitorsCapturedConfig = {
  [ANON_VISITORS_CAPTURED_LABEL]: {
    label: ANON_VISITORS_CAPTURED_LABEL,
    color: 'hsl(var(--chart-2))'
  }
} satisfies ChartConfig;

const ChartCard = ({
  title,
  description,
  data,
  config,
  dataKeys,
  operation,
  isPercentage = false,
  isLoading = false,
  descriptionClassName = '',
  totalRatio,
  tooltip
}: {
  title: string;
  description: string;
  data: TimeSeriesDataPoint[];
  config: ChartConfig;
  dataKeys: string[];
  operation?: ChartOperation;
  isPercentage?: boolean;
  isLoading?: boolean;
  descriptionClassName?: string;
  totalRatio?: string;
  tooltip?: React.ReactNode;
}) => {
  const operationResult = useMemo(() => {
    if (!operation || !data.length) return null;

    const values = data.flatMap((d) =>
      dataKeys.map((key) => (typeof d[key] === 'number' ? d[key] : 0))
    );

    switch (operation.type) {
      case 'sum':
        return values.reduce((acc, val) => acc + val, 0);
      case 'average':
        return values.reduce((acc, val) => acc + val, 0) / values.length;
      default:
        return null;
    }
  }, [data, dataKeys, operation]);

  return (
    <Card className="rounded-lg border">
      <CardHeader className="p-4">
        {isLoading ? (
          <CardHeaderSkeleton />
        ) : (
          <>
            <div className="flex items-center gap-1">
              <CardTitle className="text-lg font-semibold text-card-foreground">{title}</CardTitle>
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent>{tooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="text-4xl font-semibold text-foreground font-dmMono">
              {totalRatio ?? ''}
            </div>
            <CardDescription className={descriptionClassName}>{description}</CardDescription>
          </>
        )}
      </CardHeader>
      <CardContent className="p-4">
        {isLoading ? (
          <ChartSkeleton />
        ) : data.length === 0 ||
          data.every((d) =>
            Object.entries(d)
              .filter(([key]) => key !== 'date')
              .every(([, value]) => value === 0)
          ) ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            No Data Available
          </div>
        ) : (
          <ChartContainer config={config} className="h-[200px] w-full">
            <LineChart data={data} margin={{ top: 10, right: 25, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey={X_AXIS_KEY}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value: string) => value}
                interval={calculateInterval(data.length)}
              />
              <YAxis
                tickFormatter={
                  isPercentage
                    ? (value) => `${(value * 100).toFixed(0)}%`
                    : (value) => formatNumber(value as number)
                }
                axisLine={false}
                tickLine={false}
                tickCount={5}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, name, props) => {
                      const typedProps = props as { color?: string };
                      return (
                        <div className="flex justify-between items-center w-full gap-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 rounded-[2px]"
                              style={{ backgroundColor: typedProps.color }}
                            />
                            <span className="text-muted-foreground">{name}</span>
                          </div>
                          <span className="flex font-mono font-medium">
                            {typeof value === 'number'
                              ? isPercentage
                                ? `${(value * 100).toFixed(0)}%`
                                : value.toLocaleString('en-US', {
                                    useGrouping: true,
                                    maximumFractionDigits: 0
                                  })
                              : value}
                          </span>
                        </div>
                      );
                    }}
                    labelFormatter={(label, payload) => {
                      const firstPayload = payload[0]?.payload as { fullDate?: string } | undefined;
                      return firstPayload?.fullDate ?? (label as string);
                    }}
                    operation={operation}
                    operationResult={operationResult}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              {dataKeys.map((key) => (
                <Line
                  key={key}
                  dataKey={key}
                  type="monotone"
                  stroke={config[key].color ?? 'hsl(var(--chart-1))'}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export function DashboardPage() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug: string }>();
  let fromDate = searchParams.get('from');
  let toDate = searchParams.get('to');

  if (!fromDate || !toDate || !slug) {
    const defaultDateRange = getDefaultDateRange();
    fromDate = format(defaultDateRange.from, 'yyyy-MM-dd');
    toDate = format(defaultDateRange.to, 'yyyy-MM-dd');
  }

  // Conversion Monitoring Data
  const {
    data: conversionMonitoringData,
    loading: conversionMonitoringLoading,
    refetch: refetchConversion,
    networkStatus: conversionMonitoringNetworkStatus
  } = useQuery<GetConversionMonitoringMetricsQuery, GetConversionMonitoringMetricsQueryVariables>(
    GET_CONVERSION_MONITORING_METRICS,
    {
      variables: {
        merchantId: slugToMerchantId(slug!),
        fromDate,
        toDate
      },
      notifyOnNetworkStatusChange: true
    }
  );

  const {
    data: visitorsRecognizedData,
    loading: visitorsRecognizedLoading,
    refetch: refetchVisitorsRecognized,
    networkStatus: visitorsRecognizedNetworkStatus
  } = useQuery<GetVisitorsRecognizedQuery, GetVisitorsRecognizedQueryVariables>(
    GET_VISITORS_RECOGNIZED,
    {
      variables: {
        merchantId: slugToMerchantId(slug!),
        fromDate,
        toDate
      },
      notifyOnNetworkStatusChange: true
    }
  );

  const {
    data: anonVisitorsCapturedData,
    loading: anonVisitorsCapturedLoading,
    refetch: refetchAnonVisitorsCaptured,
    networkStatus: anonVisitorsCapturedNetworkStatus
  } = useQuery<GetAnonVisitorsCapturedQuery, GetAnonVisitorsCapturedQueryVariables>(
    GET_ANON_VISITORS_CAPTURED,
    {
      variables: {
        merchantId: slugToMerchantId(slug!),
        fromDate,
        toDate
      },
      notifyOnNetworkStatusChange: true
    }
  );

  const {
    data: totalEventsTrackedData,
    loading: totalEventsTrackedLoading,
    refetch: refetchTotalEventsTracked,
    networkStatus: totalEventsTrackedNetworkStatus
  } = useQuery<GetTotalEventsTrackedQuery, GetTotalEventsTrackedQueryVariables>(
    GET_TOTAL_EVENTS_TRACKED,
    {
      variables: {
        merchantId: slugToMerchantId(slug!),
        fromDate,
        toDate
      },
      notifyOnNetworkStatusChange: true
    }
  );

  const {
    data: serverEventLogsData,
    loading: serverEventLogsLoading,
    refetch: refetchServerEventLogs,
    networkStatus: serverEventLogsNetworkStatus
  } = useQuery<GetServerEventLogsQuery, GetServerEventLogsQueryVariables>(GET_SERVER_EVENT_LOGS, {
    variables: { merchantId: slugToMerchantId(slug!), fromDate, toDate },
    notifyOnNetworkStatusChange: true
  });

  const isRefreshing =
    serverEventLogsNetworkStatus === NetworkStatus.refetch ||
    totalEventsTrackedNetworkStatus === NetworkStatus.refetch ||
    anonVisitorsCapturedNetworkStatus === NetworkStatus.refetch ||
    conversionMonitoringNetworkStatus === NetworkStatus.refetch ||
    visitorsRecognizedNetworkStatus === NetworkStatus.refetch ||
    serverEventLogsLoading ||
    totalEventsTrackedLoading ||
    anonVisitorsCapturedLoading ||
    conversionMonitoringLoading ||
    visitorsRecognizedLoading;

  const anonVisitorsCapturedTimeSeries = useMemo(() => {
    return anonVisitorsCapturedData?.getAnonVisitorsCaptured
      ? transformMetricToTimeSeries({
          metric: anonVisitorsCapturedData.getAnonVisitorsCaptured,
          label: ANON_VISITORS_CAPTURED_LABEL
        })
      : [];
  }, [anonVisitorsCapturedData?.getAnonVisitorsCaptured]);

  const purchaseConversionTimeSeries = useMemo(() => {
    return conversionMonitoringData?.getConversionMonitoringMetrics
      ? transformMetricToTimeSeries({
          metric: conversionMonitoringData.getConversionMonitoringMetrics,
          label: PURCHASE_CONVERSION_RATE_LABEL
        })
      : [];
  }, [conversionMonitoringData?.getConversionMonitoringMetrics]);

  const visitorsRecognizedTimeSeries = useMemo(
    () =>
      visitorsRecognizedData?.getVisitorsRecognized
        ? transformMetricToTimeSeries({
            metric: visitorsRecognizedData.getVisitorsRecognized,
            label: VISITORS_RECOGNIZED_LABEL
          })
        : [],
    [visitorsRecognizedData?.getVisitorsRecognized]
  );

  const totalEventsTrackedConfig = useMemo(() => {
    if (!totalEventsTrackedData?.getTotalEventsTracked) {
      return {};
    }

    return totalEventsTrackedData.getTotalEventsTracked.reduce<ChartConfig>(
      (config, metric, index) => {
        // Only add to config if metric has non-zero data
        if (metric.aggregate > 0 || metric.data.some((point) => point > 0)) {
          config[metric.labels[0]] = {
            label: metric.labels[0],
            color: `hsl(var(--chart-${index + 1}))`
          };
        }
        return config;
      },
      {}
    );
  }, [totalEventsTrackedData?.getTotalEventsTracked]);

  const totalEventsTrackedTimeSeries = useMemo(() => {
    if (!totalEventsTrackedData?.getTotalEventsTracked) return [];

    // Filter metrics to only include those with data
    const metricsWithData = totalEventsTrackedData.getTotalEventsTracked.filter(
      (metric) => metric.aggregate > 0 || metric.data.some((point) => point > 0)
    );

    return transformMultipleMetricsToTimeSeries(metricsWithData);
  }, [totalEventsTrackedData?.getTotalEventsTracked]);

  const totalEventRatio = useMemo(() => {
    const contextualEvent = totalEventsTrackedData?.getTotalEventsTracked[0]?.aggregate ?? 0;
    const eventPixel = totalEventsTrackedData?.getTotalEventsTracked[1]?.aggregate ?? 0;

    // If the event pixel is 0, we don't want to divide by 0
    if (eventPixel === 0) {
      return 0;
    }

    const ratioData = (contextualEvent / eventPixel) * 100 || 0;
    return parseFloat(ratioData.toFixed(1));
  }, [totalEventsTrackedData]);

  const handleRefresh = async () => {
    try {
      await Promise.all([
        refetchConversion(),
        refetchVisitorsRecognized({ refresh: true }),
        refetchAnonVisitorsCaptured({ refresh: true }),
        refetchTotalEventsTracked({ refresh: true }),
        refetchServerEventLogs({ refresh: true })
      ]);
    } catch (error) {
      console.error('Error refetching queries:', error);
    }
  };

  const hasAggregateData =
    totalEventsTrackedData?.getTotalEventsTracked[0]?.aggregate !== undefined;
  const totalEventsCheck = hasAggregateData
    ? totalEventRatio === 0
      ? '-'
      : `+${totalEventRatio}%`
    : '';

  const mailsCapturedCheck =
    anonVisitorsCapturedData?.getAnonVisitorsCaptured.aggregate ||
    anonVisitorsCapturedData?.getAnonVisitorsCaptured.aggregate === 0
      ? formatNumber(anonVisitorsCapturedData.getAnonVisitorsCaptured.aggregate)
      : '';

  const liveConversionCheck =
    purchaseConversionTimeSeries.length > 0
      ? calculateAverageForPurchaseConversionRate(
          purchaseConversionTimeSeries.map((data) => data[PURCHASE_CONVERSION_RATE_LABEL] ?? 0)
        )
      : '';

  const visitorsRecognizedCheck =
    visitorsRecognizedData?.getVisitorsRecognized.aggregate ||
    visitorsRecognizedData?.getVisitorsRecognized.aggregate === 0
      ? formatNumber(visitorsRecognizedData.getVisitorsRecognized.aggregate)
      : '';

  return (
    <div className="space-y-4">
      <AppTitle>
        <AppTitleText>Dashboard</AppTitleText>
      </AppTitle>
      <AppActions>
        <RefreshButton onClick={() => void handleRefresh()} isRefreshing={isRefreshing} />
      </AppActions>
      <AppActions>
        <ControlledDatePicker />
      </AppActions>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <ChartCard
            title="Total Events Tracked"
            totalRatio={totalEventsCheck}
            description="With Contextual"
            data={totalEventsTrackedTimeSeries}
            config={totalEventsTrackedConfig}
            dataKeys={Object.keys(totalEventsTrackedConfig)}
            operation={{
              type: 'sum',
              label: 'Total Events'
            }}
            isLoading={totalEventsTrackedLoading}
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <ChartCard
            title="Visitors Recognized"
            totalRatio={visitorsRecognizedCheck}
            description="Through Contextual"
            data={visitorsRecognizedTimeSeries}
            config={visitorsRecognizedConfig}
            dataKeys={[VISITORS_RECOGNIZED_LABEL]}
            isLoading={visitorsRecognizedLoading}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <ChartCard
            title="Emails Captured"
            totalRatio={mailsCapturedCheck}
            description="From anonymous visitors"
            data={anonVisitorsCapturedTimeSeries}
            config={anonVisitorsCapturedConfig}
            dataKeys={[ANON_VISITORS_CAPTURED_LABEL]}
            isLoading={anonVisitorsCapturedLoading}
            tooltip={
              <p>
                This is a time series aggregation of the emails captured from anonymous visitors.{' '}
                <br />
                This is distinct from the snapshot view of active unique email profiles.
              </p>
            }
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <ChartCard
            title="Live Conversion Monitoring"
            totalRatio={liveConversionCheck}
            description="Match Rate"
            data={purchaseConversionTimeSeries}
            config={purchaseConversionRateConfig}
            dataKeys={[PURCHASE_CONVERSION_RATE_LABEL]}
            isPercentage={true}
            isLoading={conversionMonitoringLoading}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <ServerEventsLog
            isLoading={
              serverEventLogsLoading || serverEventLogsNetworkStatus === NetworkStatus.refetch
            }
            serverEventLogsData={serverEventLogsData}
          />
        </div>
      </div>
    </div>
  );
}
